import type { MarketVariants } from '@/lib/i18n'

export type Slide6StrangersCopy = {
  heading: string
  intro: string
  rule: string
  ctaLabel: string
  storyLabel: string
  storyTitle: string
  storySubtitle: string
  storyP1: string
  storyP2Before: string
  storyP2Initial: string
  storyP2Middle: string
  storyP2Total: string
  storyP2After: string
  storyP3: string
  callout1Heading: string
  callout1Body: string
  callout2Heading: string
  callout2Body: string
}

// Canonical US copy. GB / AU / CA override the currency-bearing fragments.
// Other strings auto-spell via useSlideCopy where appropriate.
export const copy: MarketVariants<Slide6StrangersCopy> = {
  US: {
    heading: 'Payment requests from someone you haven’t met in person',
    intro:
      'Not even if you’ve video called them. AI can now generate realistic faces, voices, and video in real time. Seeing someone on a screen is no longer proof they exist.',
    rule:
      'If you haven’t met them in person, don’t send them money. No exceptions.',
    ctaLabel: 'Read Tina’s story',
    storyLabel: 'Real story',
    storyTitle: 'The Love Trap',
    storySubtitle: 'Once upon a time, on Facebook.',
    storyP1:
      'Tina received a friend request on Facebook from a man called "David." He said he was a widowed engineer working on a contract overseas. They talked every day for three months. He was kind, patient, and never rushed her. He even video called her twice.',
    storyP2Before: 'Then his project funding fell through. He needed ',
    storyP2Initial: '$2,000',
    storyP2Middle:
      ' to get home. Tina sent it by bank transfer. Then there was a customs issue. Then a medical emergency. Over five months, Tina sent ',
    storyP2Total: '$28,000',
    storyP2After: '.',
    storyP3:
      'But David didn’t exist. The photos on his profile belonged to a real person in another country who had no idea his images were being used. The video calls had used AI deepfake technology to generate a convincing face in real time.',
    callout1Heading: 'The illusion of intimacy',
    callout1Body:
      'What makes this scam so effective is that it doesn’t feel like a scam at all. It offers warmth, attention, and companionship, especially to those who may be feeling isolated. That sense of connection is carefully manufactured, not to love you, but to lower your guard.',
    callout2Heading: 'How to protect yourself',
    callout2Body:
      'Be cautious with anyone you meet online, especially if they rush into strong feelings or talk about a future together. Never send money or gifts to someone you haven’t met in person. Real love is patient and never comes with secrecy, pressure, or money requests.',
  },
  GB: {
    storyP2Initial: '£2,000',
    storyP2Total: '£28,000',
  },
  CA: {
    storyP2Initial: '$2,000',
    storyP2Total: '$28,000',
  },
  AU: {
    storyP2Initial: '$2,000',
    storyP2Total: '$28,000',
  },
  // Qatar face. No photo ties to the name. "Sara" reads naturally across
  // Qatar's mixed population (Arab, South Asian, Filipino, Western), which
  // suits a romance story better than a distinctly Qatari name would. The
  // fake persona "David" stays: an overseas Western engineer is the classic
  // shape of this scam. Amounts in riyals.
  QA: {
    ctaLabel: 'Read Sara’s story',
    storyP1:
      'Sara received a friend request on Facebook from a man called "David." He said he was a widowed engineer working on a contract overseas. They talked every day for three months. He was kind, patient, and never rushed her. He even video called her twice.',
    storyP2Initial: 'QAR 7,000',
    storyP2Middle:
      ' to get home. Sara sent it by bank transfer. Then there was a customs issue. Then a medical emergency. Over five months, Sara sent ',
    storyP2Total: 'QAR 100,000',
  },
  ar: {
    heading: 'طلبات دفع من شخص لم تقابله شخصياً',
    intro:
      'حتى لو تحدثت معه عبر مكالمة فيديو. فالذكاء الاصطناعي قادر اليوم على توليد وجوه وأصوات ومقاطع فيديو واقعية في الوقت الفعلي. رؤية شخص على الشاشة لم تعد دليلاً على أنه موجود فعلاً.',
    rule: 'إذا لم تقابله شخصياً، فلا ترسل له المال. بلا استثناءات.',
    ctaLabel: 'اقرأ قصة سارة',
    storyLabel: 'قصة حقيقية',
    storyTitle: 'فخ الحب',
    storySubtitle: 'كان يا ما كان، على فيسبوك.',
    storyP1:
      'تلقّت سارة طلب صداقة على فيسبوك من رجل يُدعى "ديفيد". قال إنه مهندس أرمل يعمل بعقد في الخارج. تحدثا كل يوم لمدة ثلاثة أشهر. كان لطيفاً وصبوراً ولم يستعجلها قط. حتى إنه اتصل بها عبر الفيديو مرتين.',
    storyP2Before: 'ثم انهار تمويل مشروعه. وكان بحاجة إلى ',
    storyP2Initial: '7,000 ريال قطري',
    storyP2Middle:
      ' ليعود إلى بلده. أرسلتها سارة بتحويل مصرفي. ثم ظهرت مشكلة في الجمارك. ثم حالة طبية طارئة. وعلى مدى خمسة أشهر، أرسلت سارة ',
    storyP2Total: '100,000 ريال قطري',
    storyP2After: '.',
    storyP3:
      'لكن ديفيد لم يكن موجوداً. فالصور على حسابه تعود لشخص حقيقي في بلد آخر لا يعلم شيئاً عن استخدام صوره. أما مكالمات الفيديو فاستُخدمت فيها تقنية التزييف العميق بالذكاء الاصطناعي لتوليد وجه مقنع في الوقت الفعلي.',
    callout1Heading: 'وهم القرب',
    callout1Body:
      'ما يجعل هذا الاحتيال فعّالاً إلى هذا الحد هو أنه لا يبدو احتيالاً على الإطلاق. فهو يقدّم الدفء والاهتمام والرفقة، خصوصاً لمن قد يشعر بالوحدة. هذا الإحساس بالتواصل مصنوع بعناية، لا ليحبك، بل ليُضعف حذرك.',
    callout2Heading: 'كيف تحمي نفسك',
    callout2Body:
      'كن حذراً مع أي شخص تتعرف عليه عبر الإنترنت، خصوصاً إذا أسرع في إظهار مشاعر قوية أو تحدّث عن مستقبل مشترك. لا ترسل أبداً مالاً أو هدايا لشخص لم تقابله شخصياً. الحب الحقيقي صبور، ولا يأتي أبداً مصحوباً بالسرية أو الضغط أو طلبات المال.',
  },
}
